import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  makeStyles,
  Text,
  Badge,
  Button,
  Spinner,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Field,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  ArrowDownloadRegular,
  DismissCircleRegular,
  ArrowSyncRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { ContentCard } from '../../../components/layout';
import { PermissionGate } from '../../../components/common';
import {
  ApiClient,
  getErrorMessage,
  newToast,
  fullDate,
  API_URL,
} from '../../../configurations/index.config';
import type { QRAsset, QRAssignmentHistory } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#323130',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    gap: '24px',
  },
  qrPreview: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  qrImage: {
    width: '100%',
    border: '2px solid #edebe9',
    borderRadius: '8px',
  },
  infoSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  infoRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    paddingBottom: '12px',
    borderBottom: '1px solid #edebe9',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  },
  tableHeader: {
    backgroundColor: '#faf9f8',
    borderBottom: '1px solid #edebe9',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #edebe9',
  },
  td: {
    padding: '12px 16px',
    color: '#323130',
  },
  currentBadge: {
    backgroundColor: '#dff6dd',
    color: '#107c10',
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 16px',
    color: '#605e5c',
  },
});

const QRAssetDetailsFluentUI: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [qrAsset, setQrAsset] = useState<QRAsset | null>(null);
  const [history, setHistory] = useState<QRAssignmentHistory[]>([]);

  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [unassignReason, setUnassignReason] = useState('');
  const [unassignStatus, setUnassignStatus] = useState<'unassigned' | 'damaged' | 'retired'>(
    'unassigned'
  );
  const [unassigning, setUnassigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [assetRes, historyRes] = await Promise.all([
        ApiClient.get(`/get-qr-asset/${id}`),
        ApiClient.get(`/get-qr-assignment-history/${id}`),
      ]);

      if (assetRes.data.status === 'success') {
        setQrAsset(assetRes.data.data);
      }

      if (historyRes.data.status === 'success') {
        setHistory(historyRes.data.data || []);
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
      navigate('/account/qr-assets');
    }
    setLoading(false);
  };

  const handleDownload = () => {
    if (qrAsset) {
      const imageUrl = `${API_URL}${qrAsset.qr_image_url}`;
      const a = document.createElement('a');
      a.href = imageUrl;
      a.download = `${qrAsset.qr_name}.png`;
      a.click();
    }
  };

  const handleUnassign = async () => {
    if (!qrAsset) {
      return;
    }

    setUnassigning(true);
    try {
      const response = await ApiClient.post(`/unassign-qr/${qrAsset.id}`, {
        reason: unassignReason || undefined,
        new_status: unassignStatus,
      });

      if (response.data.status === 'success') {
        newToast('success', 'QR code unassigned successfully');
        setUnassignDialogOpen(false);
        setUnassignReason('');
        fetchData();
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setUnassigning(false);
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      unassigned: { color: 'warning' as const, text: 'Unassigned' },
      assigned: { color: 'success' as const, text: 'Assigned' },
      damaged: { color: 'danger' as const, text: 'Damaged' },
      retired: { color: 'subtle' as const, text: 'Retired' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.unassigned;
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
        <Spinner label="Loading QR asset..." />
      </div>
    );
  }

  if (!qrAsset) {
    return null;
  }

  return (
    <PermissionGate privilege="qr-assets.view" showDenied>
      <div className={styles.container}>
        <div className={styles.pageHeader}>
          <Button
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            onClick={() => navigate('/account/qr-assets')}
          />
          <Text className={styles.pageTitle}>{qrAsset.qr_name}</Text>
          {getStatusBadge(qrAsset.status)}
        </div>

        <div className={styles.grid}>
          <ContentCard>
            <div className={styles.qrPreview}>
              <img
                src={`${API_URL}${qrAsset.qr_image_url}`}
                alt={qrAsset.qr_name}
                className={styles.qrImage}
              />
              <div className={styles.actions}>
                <PermissionGate privilege="qr-assets.download">
                  <Button
                    appearance="secondary"
                    icon={<ArrowDownloadRegular />}
                    onClick={handleDownload}
                  >
                    Download
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </ContentCard>

          <ContentCard>
            <div className={styles.infoSection}>
              <Text size={500} weight="semibold">
                QR Asset Information
              </Text>

              <div className={styles.infoRow}>
                <Text size={200} style={{ color: '#605e5c' }}>
                  QR Name
                </Text>
                <Text weight="semibold">{qrAsset.qr_name}</Text>
              </div>

              <div className={styles.infoRow}>
                <Text size={200} style={{ color: '#605e5c' }}>
                  QR Token
                </Text>
                <Text style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                  {qrAsset.qr_token}
                </Text>
              </div>

              <div className={styles.infoRow}>
                <Text size={200} style={{ color: '#605e5c' }}>
                  Batch
                </Text>
                <Text>
                  Batch {qrAsset.batch_number} - Sequence {qrAsset.batch_sequence}
                </Text>
              </div>

              <div className={styles.infoRow}>
                <Text size={200} style={{ color: '#605e5c' }}>
                  Status
                </Text>
                {getStatusBadge(qrAsset.status)}
              </div>

              {qrAsset.assigned_checkpoint_name && (
                <>
                  <div className={styles.infoRow}>
                    <Text size={200} style={{ color: '#605e5c' }}>
                      Assigned Checkpoint
                    </Text>
                    <Text weight="semibold">{qrAsset.assigned_checkpoint_name}</Text>
                  </div>

                  <div className={styles.infoRow}>
                    <Text size={200} style={{ color: '#605e5c' }}>
                      Assigned Site
                    </Text>
                    <Text weight="semibold">{qrAsset.assigned_site_name}</Text>
                  </div>

                  <div className={styles.infoRow}>
                    <Text size={200} style={{ color: '#605e5c' }}>
                      Assigned At
                    </Text>
                    <Text>{qrAsset.assigned_at ? fullDate(qrAsset.assigned_at) : 'N/A'}</Text>
                  </div>

                  <div className={styles.infoRow}>
                    <Text size={200} style={{ color: '#605e5c' }}>
                      Assigned By
                    </Text>
                    <Text>{qrAsset.assigner_name || 'Unknown'}</Text>
                  </div>
                </>
              )}

              <div className={styles.infoRow}>
                <Text size={200} style={{ color: '#605e5c' }}>
                  Total Scans
                </Text>
                <Text weight="semibold">{qrAsset.total_scans || 0}</Text>
              </div>

              <div className={styles.infoRow}>
                <Text size={200} style={{ color: '#605e5c' }}>
                  Created
                </Text>
                <Text>{fullDate(qrAsset.created_at)}</Text>
              </div>

              <div className={styles.infoRow}>
                <Text size={200} style={{ color: '#605e5c' }}>
                  Created By
                </Text>
                <Text>{qrAsset.creator_name || 'Unknown'}</Text>
              </div>

              {qrAsset.notes && (
                <div className={styles.infoRow}>
                  <Text size={200} style={{ color: '#605e5c' }}>
                    Notes
                  </Text>
                  <Text>{qrAsset.notes}</Text>
                </div>
              )}

              <div className={styles.actions} style={{ marginTop: '16px' }}>
                {qrAsset.status === 'assigned' && (
                  <PermissionGate privilege="qr-assets.unassign">
                    <Dialog
                      open={unassignDialogOpen}
                      onOpenChange={(_, data) => setUnassignDialogOpen(data.open)}
                    >
                      <DialogTrigger disableButtonEnhancement>
                        <Button appearance="secondary" icon={<DismissCircleRegular />}>
                          Unassign
                        </Button>
                      </DialogTrigger>
                      <DialogSurface>
                        <DialogBody>
                          <DialogTitle>Unassign QR Code</DialogTitle>
                          <DialogContent>
                            <Field label="New Status" required>
                              <select
                                value={unassignStatus}
                                onChange={e =>
                                  setUnassignStatus(
                                    e.target.value as 'unassigned' | 'damaged' | 'retired'
                                  )
                                }
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  fontSize: '14px',
                                  border: '1px solid #d1d1d1',
                                  borderRadius: '4px',
                                }}
                              >
                                <option value="unassigned">Unassigned (for reassignment)</option>
                                <option value="damaged">Damaged (needs replacement)</option>
                                <option value="retired">Retired (permanently)</option>
                              </select>
                            </Field>
                            <Field label="Reason" hint="Optional unassignment reason">
                              <textarea
                                value={unassignReason}
                                onChange={e => setUnassignReason(e.target.value)}
                                rows={3}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  fontFamily: 'inherit',
                                  fontSize: '14px',
                                  border: '1px solid #d1d1d1',
                                  borderRadius: '4px',
                                }}
                              />
                            </Field>
                          </DialogContent>
                          <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                              <Button appearance="secondary">Cancel</Button>
                            </DialogTrigger>
                            <Button
                              appearance="primary"
                              onClick={handleUnassign}
                              disabled={unassigning}
                            >
                              {unassigning ? 'Unassigning...' : 'Unassign'}
                            </Button>
                          </DialogActions>
                        </DialogBody>
                      </DialogSurface>
                    </Dialog>
                  </PermissionGate>
                )}

                {qrAsset.status === 'assigned' && (
                  <PermissionGate privilege="qr-assets.assign">
                    <Button
                      appearance="secondary"
                      icon={<ArrowSyncRegular />}
                      onClick={() => navigate(`/account/reassign-qr/${qrAsset.id}`)}
                    >
                      Reassign
                    </Button>
                  </PermissionGate>
                )}

                {qrAsset.status === 'unassigned' && (
                  <PermissionGate privilege="qr-assets.assign">
                    <Button
                      appearance="primary"
                      onClick={() => navigate(`/account/assign-qr/${qrAsset.id}`)}
                    >
                      Assign to Checkpoint
                    </Button>
                  </PermissionGate>
                )}
              </div>
            </div>
          </ContentCard>
        </div>

        <ContentCard>
          <Text size={500} weight="semibold" style={{ display: 'block', marginBottom: '16px' }}>
            Assignment History
          </Text>

          {history.length === 0 && qrAsset.status !== 'assigned' ? (
            <div className={styles.emptyState}>
              <WarningRegular style={{ fontSize: '48px', marginBottom: '8px' }} />
              <Text>No assignment history yet</Text>
            </div>
          ) : (
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th className={styles.th}>Checkpoint</th>
                  <th className={styles.th}>Site</th>
                  <th className={styles.th}>Assigned</th>
                  <th className={styles.th}>Assigned By</th>
                  <th className={styles.th}>Unassigned</th>
                  <th className={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {qrAsset.status === 'assigned' && qrAsset.assigned_checkpoint_name && (
                  <tr className={styles.tr}>
                    <td className={styles.td}>
                      <Text weight="semibold">{qrAsset.assigned_checkpoint_name}</Text>
                    </td>
                    <td className={styles.td}>{qrAsset.assigned_site_name || '-'}</td>
                    <td className={styles.td}>
                      {qrAsset.assigned_at ? fullDate(qrAsset.assigned_at) : '-'}
                    </td>
                    <td className={styles.td}>{qrAsset.assigner_name || '-'}</td>
                    <td className={styles.td}>-</td>
                    <td className={styles.td}>
                      <span className={styles.currentBadge}>Current</span>
                    </td>
                  </tr>
                )}
                {history.map((item, index) => (
                  <tr key={index} className={styles.tr}>
                    <td className={styles.td}>
                      <Text weight="semibold">{item.checkpoint_name}</Text>
                    </td>
                    <td className={styles.td}>{item.site_name || '-'}</td>
                    <td className={styles.td}>{fullDate(item.assigned_at)}</td>
                    <td className={styles.td}>{item.assigner_name || '-'}</td>
                    <td className={styles.td}>
                      {item.unassigned_at ? (
                        <div>
                          <Text size={200}>{fullDate(item.unassigned_at)}</Text>
                          {item.unassigner_name && (
                            <Text size={200} style={{ color: '#605e5c', display: 'block' }}>
                              by {item.unassigner_name}
                            </Text>
                          )}
                          {item.unassign_reason && (
                            <Text
                              size={200}
                              style={{ color: '#605e5c', display: 'block', fontStyle: 'italic' }}
                            >
                              {item.unassign_reason}
                            </Text>
                          )}
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className={styles.td}>
                      <Badge color="subtle">Past</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ContentCard>
      </div>
    </PermissionGate>
  );
};

export default QRAssetDetailsFluentUI;
